import React, { useRef } from 'react';
import ringArt from '../../../assets/img/portal-ring.png';
import { FrameStrip, fmtClip } from './StepTrim';
import { PILL_BASE } from './Sheet';

/**
 * 02 · COVER — which frame people meet the Portal as, and where the ring should look in it
 * (handoff v3 §4). Video only; a photo is its own cover and text has none.
 *
 * The strip is the same eight frames as TRIM with everything outside the window dimmed; a
 * lifted 40px selector shows the captured frame. Dragging anywhere on the strip clamps to the
 * trim window and the frame is captured on pointer-up. Beside it the ring itself: drag the
 * picture inside it, and the part you leave showing is what lands on everyone's rail.
 */
const RING = 104;

/** The ring, the cover in its face, and a drag that moves the focal point. */
export function AimRing({ cover, focus, onFocus, onSettle = null, size = RING }) {
  const drag = useRef(null);
  const f = focus || { x: 50, y: 50 };
  const onPointerDown = (e) => {
    e.preventDefault();
    const W = e.currentTarget.getBoundingClientRect().width || size;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const from = f;
    drag.current = true;
    // Drag right, the picture moves right — which means looking further LEFT in the frame.
    const move = (ev) => onFocus({
      x: Math.max(0, Math.min(100, from.x - ((ev.clientX - x0) / W) * 100)),
      y: Math.max(0, Math.min(100, from.y - ((ev.clientY - y0) / W) * 100)),
    });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      drag.current = false;
      onSettle?.();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  return (
    <div
      role="application"
      aria-label="Drag to position the cover in the ring"
      onPointerDown={onPointerDown}
      style={{ position: 'relative', flexShrink: 0, width: size, height: size, cursor: 'grab', touchAction: 'none' }}
    >
      <span aria-hidden style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: 'prcSpin 6s linear infinite', pointerEvents: 'none' }} />
      <span style={{ position: 'absolute', inset: '5.5%', borderRadius: '50%', overflow: 'hidden', background: '#000', pointerEvents: 'none' }}>
        {cover && <img src={cover} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${f.x}% ${f.y}%`, display: 'block' }} />}
      </span>
    </div>
  );
}

export default function StepCover({
  frames,
  durationSec,          // the whole file
  trim,                 // { startMs, endMs } — the window the cover must come from
  coverPct,             // 0–1 through the window
  onCoverPct,
  onCoverSettled = null, // pointer-up: capture the frame
  cover,
  focus,
  onFocus,
}) {
  const stripRef = useRef(null);
  const d = durationSec || 0;
  const s = (trim?.startMs || 0) / 1000;
  const e = trim && trim.endMs > trim.startMs ? trim.endMs / 1000 : d;
  const span = Math.max(0, e - s);
  const pct = (sec) => (d ? (sec / d) * 100 : 0);
  const at = d ? pct(s + span * coverPct) : 30;

  const onPointerDown = (ev) => {
    if (ev.button != null && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    const r = stripRef.current.getBoundingClientRect();
    const set = (x) => {
      const t = Math.max(s, Math.min(e, ((x - r.left) / r.width) * d));
      onCoverPct(span > 0 ? (t - s) / span : 0);
    };
    set(ev.clientX);
    const move = (mv) => set(mv.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      onCoverSettled?.();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  return (
    <div className="font-mono text-white" style={{ display: 'grid', gap: 12 }} data-testid="step-cover">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>
        <span>CHOOSE A COVER</span>
        <span style={{ color: '#00FFFF' }}>{fmtClip(span * coverPct)} / {fmtClip(span)}</span>
      </div>
      <span style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,.7)' }}>
        This frame is what people see on your ring and on the share card. It comes from inside your clip.
      </span>

      <FrameStrip frames={frames} stripRef={stripRef} data-testid="cover-strip" onPointerDown={onPointerDown} style={{ overflow: 'visible', cursor: 'pointer' }}>
        <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct(s)}%`, background: 'rgba(0,0,0,.8)', borderRadius: '10px 0 0 10px', pointerEvents: 'none' }} />
        <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${100 - pct(e)}%`, background: 'rgba(0,0,0,.8)', borderRadius: '0 10px 10px 0', pointerEvents: 'none' }} />
        <div aria-hidden data-testid="cover-selector" style={{ position: 'absolute', top: -6, bottom: -6, left: `calc(${at}% - 20px)`, width: 40, border: '3px solid #fff', borderRadius: 8, overflow: 'hidden', background: '#000', pointerEvents: 'none', boxSizing: 'border-box' }}>
          {cover && <img src={cover} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </div>
      </FrameStrip>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4 }}>
        <AimRing cover={cover} focus={focus} onFocus={onFocus} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>AIM IT IN THE RING</span>
          <span style={{ fontSize: 11, lineHeight: 1.45, color: 'rgba(255,255,255,.7)' }}>Drag the picture. What you leave showing is what lands on everyone’s rail.</span>
          <button type="button" onClick={() => onFocus({ x: 50, y: 50 })} className="prc-pill" style={{ ...PILL_BASE, alignSelf: 'flex-start', height: 28, padding: '0 12px', background: 'rgba(255,255,255,.12)', color: '#fff', fontSize: 10, letterSpacing: '0.12em' }}>
            CENTRE IT
          </button>
        </div>
      </div>
    </div>
  );
}
